import React, { useMemo, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import {
  parseClinicalCriteriaContent,
  type ClinicalCriteriaSection,
} from "../../utils/encoder-clinical-criteria";
import { sanitizeGuidelineHtml } from "../../utils/guideline-markdown";

const CriteriaSection: React.FC<{ section: ClinicalCriteriaSection }> = ({
  section,
}) => {
  const [open, setOpen] = useState(true);
  const html = useMemo(
    () => sanitizeGuidelineHtml(section.html),
    [section.html]
  );

  return (
    <details
      className="clinical-criteria-accordion group overflow-hidden rounded-lg border border-blue-200"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="clinical-criteria-accordion-summary cursor-pointer list-none bg-blue-50 text-gray-900">
        <div className="flex items-center gap-2 px-2.5 py-1.5">
          <IconChevronDown
            size={16}
            className="clinical-criteria-chevron shrink-0 text-blue-600 transition-transform"
          />
          <span className="text-[17px] font-semibold leading-snug">
            {section.title}
          </span>
        </div>
      </summary>
      {open ? (
        <div className="border-t border-blue-200 bg-white px-2.5 pb-2.5 pt-1">
          <div
            className="medicalContainer max-w-none rounded-lg bg-white/80 px-2.5 py-2"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      ) : null}
    </details>
  );
};

const ClinicalCriteriaAccordion: React.FC<{
  content: string;
  fileName?: string | null;
}> = ({ content, fileName }) => {
  const parsed = useMemo(
    () => parseClinicalCriteriaContent(content, fileName || undefined),
    [content, fileName]
  );

  return (
    <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-2">
      <h3 className="px-2 py-2 text-lg font-bold leading-snug text-gray-900">
        {parsed.title}
      </h3>
      {parsed.sections.length === 0 ? (
        <p className="px-2 pb-2 text-center text-sm text-slate-500">
          No clinical criteria content is available for this document.
        </p>
      ) : (
        <div className="space-y-2">
          {parsed.sections.map((section) => (
            <CriteriaSection key={section.id} section={section} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ClinicalCriteriaAccordion;
